import React, { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { FiCheck, FiClipboard, FiCopy, FiMessageSquare, FiRefreshCw, FiUsers } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { subscribeAssignments, subscribeClients, subscribeUsers } from '../services/assignmentService';
import { subscribeQuickWorks } from '../services/quickWorkService';
import { subscribeMatters } from '../services/matterService';
import { subscribeProformas } from '../services/billingService';
import { ageingBucket, daysSince, money, outstandingOf, toDate } from '../utils/billingUtils';

const SYSTEM_FOOTER = '*System-generated communication via Pravah.*';

function startOfDay(value = new Date()) {
  const date = new Date(value);
  date.setHours(0, 0, 0, 0);
  return date;
}

function addDays(value, days) {
  const date = startOfDay(value);
  date.setDate(date.getDate() + days);
  return date;
}

function sameDay(value, reference) {
  const date = toDate(value);
  if (!date) return false;
  return startOfDay(date).getTime() === startOfDay(reference).getTime();
}

function beforeDay(value, reference) {
  const date = toDate(value);
  if (!date) return false;
  return startOfDay(date).getTime() < startOfDay(reference).getTime();
}

function betweenDays(value, start, end) {
  const date = toDate(value);
  if (!date) return false;
  const stamp = startOfDay(date).getTime();
  return stamp >= startOfDay(start).getTime() && stamp <= startOfDay(end).getTime();
}

function dateLabel(value) {
  const date = toDate(value);
  return date ? date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—';
}

function dateTimeLabel(value) {
  const date = toDate(value);
  return date ? date.toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';
}

function itemName(row = {}) {
  return row.assignmentType || row.category || row.nature || row.title || row.reference || 'Work item';
}

function isUsefulClientName(value) {
  const name = String(value || '').trim();
  if (!name) return false;
  return !['client', 'client unavailable', 'unknown client', 'unknown', 'n/a', '-', '—'].includes(name.toLowerCase());
}

function sameNonEmpty(a, b) {
  const left = String(a || '').trim();
  const right = String(b || '').trim();
  return Boolean(left && right && left === right);
}

function resolveClientName(row = {}, clients = []) {
  const directCandidates = [
    row.clientName,
    row.client?.name,
    row.client?.clientName,
    row.client_name,
  ];
  const direct = directCandidates.find(isUsefulClientName);
  if (direct) return String(direct).trim();

  const legacyClientValue = typeof row.client === 'string' ? row.client.trim() : '';
  const clientId = row.clientId || row.clientID || row.client?.id || row.client?.clientId || '';
  const found = clients.find((client) =>
    sameNonEmpty(client.id, clientId) ||
    sameNonEmpty(client.clientId, clientId) ||
    sameNonEmpty(client.clientCode, clientId) ||
    sameNonEmpty(client.id, legacyClientValue) ||
    sameNonEmpty(client.clientId, legacyClientValue) ||
    sameNonEmpty(client.clientCode, legacyClientValue) ||
    (isUsefulClientName(legacyClientValue) && (sameNonEmpty(client.name, legacyClientValue) || sameNonEmpty(client.clientName, legacyClientValue)))
  );

  const masterName = found?.name || found?.clientName;
  if (isUsefulClientName(masterName)) return String(masterName).trim();
  if (isUsefulClientName(legacyClientValue)) return legacyClientValue;
  return 'Client';
}

function isCompleted(row = {}) {
  return ['completed', 'complete', 'done', 'closed'].includes(String(row.status || '').trim().toLowerCase());
}

function isOpen(row = {}) {
  return !['completed', 'complete', 'done', 'closed', 'cancelled', 'canceled'].includes(String(row.status || '').trim().toLowerCase());
}

function recipientPhone(value) {
  return String(value || '').replace(/\D/g, '').replace(/^0+/, '');
}

function withIndiaCountryCode(value) {
  const digits = recipientPhone(value);
  if (!digits) return '';
  if (digits.length === 10) return `91${digits}`;
  return digits;
}

function messageWithFooter(body) {
  return `${String(body || '').trim()}\n\n\n${SYSTEM_FOOTER}`;
}

async function copyText(text) {
  await navigator.clipboard.writeText(text);
}

function Section({ title, children, emptyText = 'Nothing requiring attention.' }) {
  const rows = React.Children.toArray(children).filter(Boolean);
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
      <div className="mt-3 space-y-2">
        {rows.length ? rows : <p className="text-sm text-slate-500">{emptyText}</p>}
      </div>
    </section>
  );
}

function BriefCard({ title, subtitle, text, onCopy, copied }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 p-4">
        <div>
          <h2 className="font-semibold text-slate-900">{title}</h2>
          <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
        </div>
        <button type="button" onClick={onCopy} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50">
          {copied ? <FiCheck /> : <FiCopy />}{copied ? 'Copied' : 'Copy Message'}
        </button>
      </div>
      <pre className="whitespace-pre-wrap break-words p-4 font-sans text-sm leading-6 text-slate-800">{text}</pre>
    </div>
  );
}

export default function CommunicationCentre() {
  const { userData } = useAuth();
  const role = String(userData?.role || '').toLowerCase();
  const allowed = role === 'partner' && userData?.communicationCentreEnabled === true;

  const [assignments, setAssignments] = useState([]);
  const [quickWorks, setQuickWorks] = useState([]);
  const [matters, setMatters] = useState([]);
  const [proformas, setProformas] = useState([]);
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState('');
  const [tab, setTab] = useState('partner');
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    if (!allowed) return undefined;
    const unsubscribers = [
      subscribeAssignments({ role: 'partner', userId: userData?.id, onData: setAssignments, onError: console.error }),
      subscribeQuickWorks({ role: 'partner', userId: userData?.id, onData: setQuickWorks, onError: console.error }),
      subscribeMatters({ role: 'partner', userId: userData?.id, onData: setMatters, onError: console.error }),
      subscribeProformas(setProformas, console.error),
      subscribeUsers(setUsers, console.error),
      subscribeClients(setClients, console.error),
    ];
    const timer = window.setTimeout(() => setLoading(false), 350);
    return () => {
      window.clearTimeout(timer);
      unsubscribers.forEach((unsubscribe) => unsubscribe?.());
    };
  }, [allowed, userData?.id]);

  const today = useMemo(() => startOfDay(new Date()), [refreshTick]);
  const tomorrow = useMemo(() => addDays(today, 1), [today]);
  const nextThreeDays = useMemo(() => addDays(today, 3), [today]);
  const yesterday = useMemo(() => addDays(today, -1), [today]);

  const openWork = useMemo(() => [...assignments, ...quickWorks].filter(isOpen), [assignments, quickWorks]);
  const dueToday = useMemo(() => openWork.filter((row) => sameDay(row.dueDate, today)), [openWork, today]);
  const overdue = useMemo(() => openWork.filter((row) => row.dueDate && beforeDay(row.dueDate, today)), [openWork, today]);
  const dueTomorrow = useMemo(() => openWork.filter((row) => sameDay(row.dueDate, tomorrow)), [openWork, tomorrow]);

  const replyDue = useMemo(() => matters.filter((matter) => matter.replyDueDate && !matter.replyFiledDate && betweenDays(matter.replyDueDate, today, nextThreeDays)), [matters, today, nextThreeDays]);
  const hearings = useMemo(() => matters.filter((matter) => matter.nextHearingDate && betweenDays(matter.nextHearingDate, today, tomorrow)), [matters, today, tomorrow]);

  const activeBills = useMemo(() => proformas.filter((item) => String(item.status || '').toLowerCase() !== 'cancelled'), [proformas]);
  const outstandingBills = useMemo(() => activeBills.map((item) => ({ ...item, balance: outstandingOf(item) })).filter((item) => item.balance > 0.009), [activeBills]);
  const highValueThreshold = 50000;
  const highValue = useMemo(() => outstandingBills.filter((item) => item.balance >= highValueThreshold).sort((a, b) => b.balance - a.balance).slice(0, 5), [outstandingBills]);
  const oldOutstanding = useMemo(() => outstandingBills.filter((item) => (daysSince(item.proformaDate || item.createdAt) || 0) >= 60).sort((a, b) => (daysSince(b.proformaDate || b.createdAt) || 0) - (daysSince(a.proformaDate || a.createdAt) || 0)).slice(0, 5), [outstandingBills]);
  const followUpsDue = useMemo(() => outstandingBills.filter((item) => item.nextFollowUpDate && !afterToday(item.nextFollowUpDate, today)).slice(0, 8), [outstandingBills, today]);
  const unbilled = useMemo(() => [...assignments, ...quickWorks].filter((row) => isCompleted(row) && !row.proformaId && String(row.billingStatus || '').toLowerCase() !== 'paid'), [assignments, quickWorks]);

  const completedToday = useMemo(() => [...assignments, ...quickWorks].filter((row) => isCompleted(row) && sameDay(row.completedAt || row.updatedAt, today)).slice(0, 8), [assignments, quickWorks, today]);
  const completedYesterday = useMemo(() => [...assignments, ...quickWorks].filter((row) => isCompleted(row) && sameDay(row.completedAt || row.updatedAt, yesterday)).slice(0, 6), [assignments, quickWorks, yesterday]);
  const billsRaisedToday = useMemo(() => activeBills.filter((row) => sameDay(row.proformaDate || row.createdAt, today)).slice(0, 6), [activeBills, today]);
  const paymentsToday = useMemo(() => activeBills.flatMap((bill) => (bill.payments || []).filter((payment) => sameDay(payment.date || payment.addedAt, today)).map((payment) => ({ bill, payment }))).slice(0, 8), [activeBills, today]);

  const staffAlerts = useMemo(() => {
    const recentCutoff = addDays(today, -1);
    const alerts = [];
    assignments.filter((row) => row.createdAt && betweenDays(row.createdAt, recentCutoff, today)).forEach((row) => {
      alerts.push({ type: 'assigned', row, staffId: row.assignedToId, text: `New Assignment: ${resolveClientName(row, clients)} – ${itemName(row)}${row.dueDate ? `\nDue: ${dateLabel(row.dueDate)}` : ''}` });
    });
    dueTomorrow.forEach((row) => alerts.push({ type: 'due', row, staffId: row.assignedToId, text: `Reminder: ${resolveClientName(row, clients)} – ${itemName(row)} is due tomorrow.${row.dueDate ? `\nDue: ${dateLabel(row.dueDate)}` : ''}` }));
    overdue.slice(0, 12).forEach((row) => alerts.push({ type: 'overdue', row, staffId: row.assignedToId, text: `Overdue work: ${resolveClientName(row, clients)} – ${itemName(row)}${row.dueDate ? `\nDue date: ${dateLabel(row.dueDate)}` : ''}` }));
    return alerts.slice(0, 20);
  }, [assignments, dueTomorrow, overdue, today, clients]);

  const clientMessages = useMemo(() => {
    const rows = [...assignments, ...quickWorks].filter((row) => isCompleted(row) && sameDay(row.completedAt || row.updatedAt, today));
    return rows.slice(0, 15).map((row) => ({ row, client: clients.find((client) => client.id === row.clientId), text: `Dear Sir/Ma'am, the work relating to ${itemName(row)} has been completed.\nClient: ${resolveClientName(row, clients)}\nThank you.` }));
  }, [assignments, quickWorks, clients, today]);

  const morningBrief = useMemo(() => buildMorningBrief({ today, dueToday, overdue, replyDue, hearings, unbilled, followUpsDue, highValue, oldOutstanding, completedYesterday, outstandingBills, clients }), [today, dueToday, overdue, replyDue, hearings, unbilled, followUpsDue, highValue, oldOutstanding, completedYesterday, outstandingBills, clients]);
  const eveningBrief = useMemo(() => buildEveningBrief({ today, completedToday, billsRaisedToday, paymentsToday, overdue, dueTomorrow, hearings, unbilled, followUpsDue, outstandingBills, clients }), [today, completedToday, billsRaisedToday, paymentsToday, overdue, dueTomorrow, hearings, unbilled, followUpsDue, outstandingBills, clients]);

  if (userData && !allowed) return <Navigate to="/dashboard" replace />;

  const copy = async (key, text) => {
    await copyText(text);
    setCopied(key);
    window.setTimeout(() => setCopied(''), 1800);
  };

  const openWhatsApp = (phone, body) => {
    const number = withIndiaCountryCode(phone);
    if (!number) {
      alert('No valid mobile number is saved for this recipient.');
      return;
    }
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(messageWithFooter(body))}`, '_blank', 'noopener,noreferrer');
  };

  const tabs = [
    { id: 'partner', label: 'Partner Briefs', icon: FiClipboard },
    { id: 'staff', label: 'Staff Messages', icon: FiUsers },
    { id: 'client', label: 'Client Messages', icon: FiMessageSquare },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Communication Centre</h1>
          <p className="mt-1 text-sm text-slate-600">Pravah prepares the communication. You review and send it manually.</p>
        </div>
        <button type="button" onClick={() => setRefreshTick((value) => value + 1)} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"><FiRefreshCw />Refresh brief</button>
      </div>

      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <strong>Private Partner workspace.</strong> This page is available only to the Partner specifically enabled in Staff Master. Messages are auto-generated from Pravah data but are never sent automatically.
      </div>

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {tabs.map(({ id, label, icon: Icon }) => <button key={id} type="button" onClick={() => setTab(id)} className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium ${tab === id ? 'bg-slate-900 text-white' : 'bg-white text-slate-700 border border-slate-200'}`}><Icon />{label}</button>)}
      </div>

      {loading ? <div className="rounded-xl border bg-white p-6 text-sm text-slate-500">Preparing Communication Centre…</div> : null}

      {!loading && tab === 'partner' && (
        <div className="grid gap-5 xl:grid-cols-2">
          <BriefCard title="Morning Partner Brief" subtitle="Due work, matters, billing exceptions and recent progress." text={morningBrief} copied={copied === 'morning'} onCopy={() => copy('morning', morningBrief)} />
          <BriefCard title="Evening Partner Brief" subtitle="What moved today, what remains pending, and tomorrow's attention." text={eveningBrief} copied={copied === 'evening'} onCopy={() => copy('evening', eveningBrief)} />
        </div>
      )}

      {!loading && tab === 'staff' && (
        <div className="space-y-3">
          {staffAlerts.length ? staffAlerts.map((alert, index) => {
            const staff = users.find((member) => member.id === alert.staffId);
            const body = `${alert.text}\nAssigned to: ${staff?.name || alert.row.assignedToName || 'Staff'}`;
            return <div key={`${alert.type}-${alert.row.id}-${index}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><div className="flex flex-wrap items-start justify-between gap-3"><div><div className="text-sm font-semibold text-slate-900">{staff?.name || alert.row.assignedToName || 'Staff'}</div><pre className="mt-2 whitespace-pre-wrap font-sans text-sm leading-6 text-slate-700">{messageWithFooter(body)}</pre></div><div className="flex gap-2"><button type="button" onClick={() => copy(`staff-${index}`, messageWithFooter(body))} className="rounded border border-slate-300 px-3 py-2 text-sm">{copied === `staff-${index}` ? 'Copied' : 'Copy'}</button><button type="button" onClick={() => openWhatsApp(staff?.mobile, body)} className="rounded bg-emerald-700 px-3 py-2 text-sm font-medium text-white">Open WhatsApp</button></div></div></div>;
          }) : <div className="rounded-xl border bg-white p-6 text-sm text-slate-500">No staff messages are suggested right now.</div>}
        </div>
      )}

      {!loading && tab === 'client' && (
        <div className="space-y-3">
          <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-900">Client messages are suggestions only. Review the wording before sending. Internal matter notes are never included.</div>
          {clientMessages.length ? clientMessages.map(({ row, client, text }, index) => {
            const primary = (client?.additionalContacts || []).find((item) => item.isPrimary && item.mobile) || (client?.additionalContacts || []).find((item) => item.mobile);
            const mobile = primary?.mobile || client?.mobile || '';
            return <div key={`${row.id}-${index}`} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm"><div className="flex flex-wrap items-start justify-between gap-3"><div><div className="text-sm font-semibold text-slate-900">{resolveClientName(row, clients) || client?.name || 'Client'}</div><pre className="mt-2 whitespace-pre-wrap font-sans text-sm leading-6 text-slate-700">{messageWithFooter(text)}</pre></div><div className="flex gap-2"><button type="button" onClick={() => copy(`client-${index}`, messageWithFooter(text))} className="rounded border border-slate-300 px-3 py-2 text-sm">{copied === `client-${index}` ? 'Copied' : 'Copy'}</button><button type="button" onClick={() => openWhatsApp(mobile, text)} className="rounded bg-emerald-700 px-3 py-2 text-sm font-medium text-white">Open WhatsApp</button></div></div></div>;
          }) : <div className="rounded-xl border bg-white p-6 text-sm text-slate-500">No completed-work client messages are suggested today.</div>}
        </div>
      )}
    </div>
  );
}

function afterToday(value, today) {
  const date = toDate(value);
  return date ? startOfDay(date).getTime() > startOfDay(today).getTime() : false;
}

function lineForWork(row, clients = []) {
  const overdueDays = row.dueDate && beforeDay(row.dueDate, new Date()) ? daysSince(row.dueDate) : 0;
  return `• ${resolveClientName(row, clients)} – ${itemName(row)}${row.dueDate ? ` – due ${dateLabel(row.dueDate)}` : ''}${overdueDays ? ` (${overdueDays} day${overdueDays === 1 ? '' : 's'} overdue)` : ''}${row.assignedToName ? ` – ${row.assignedToName}` : ''}`;
}

function lines(rows, formatter, max = 6) {
  if (!rows.length) return ['• None'];
  const visible = rows.slice(0, max).map(formatter);
  if (rows.length > max) visible.push(`• +${rows.length - max} more in Pravah`);
  return visible;
}

function billWorkDescription(bill = {}) {
  const descriptions = (bill.lineItems || [])
    .map((line) => String(line?.description || '').trim())
    .filter(Boolean);
  if (descriptions.length) {
    const unique = [...new Set(descriptions)];
    if (unique.length <= 3) return unique.join(', ');
    return `${unique.slice(0, 3).join(', ')} +${unique.length - 3} more`;
  }
  return bill.description || bill.remarks || bill.reference || 'professional services';
}

function billDispatchLabel(bill = {}) {
  const status = String(bill.status || '').trim().toLowerCase();
  if (status === 'sent') return 'sent to client';
  if (status === 'draft') return 'draft not yet sent';
  return bill.status ? String(bill.status) : 'status not recorded';
}

function totalAmount(rows = [], getter) {
  return rows.reduce((sum, row) => sum + Number(getter(row) || 0), 0);
}

function oldOutstandingTotal(outstandingBills = [], minimumDays = 90) {
  return outstandingBills
    .filter((bill) => (daysSince(bill.proformaDate || bill.createdAt) || 0) >= minimumDays)
    .reduce((sum, bill) => sum + Number(bill.balance || 0), 0);
}

function buildMorningBrief({ today, dueToday, overdue, replyDue, hearings, unbilled, followUpsDue, highValue, oldOutstanding, completedYesterday, outstandingBills, clients = [] }) {
  const totalOutstanding = totalAmount(outstandingBills, (item) => item.balance);
  const outstanding90Plus = oldOutstandingTotal(outstandingBills, 90);
  const priorityWork = [...dueToday, ...overdue];
  const matterItems = [...hearings, ...replyDue];

  const billingRows = [];
  followUpsDue.slice(0, 4).forEach((bill) => {
    const age = daysSince(bill.proformaDate || bill.createdAt);
    billingRows.push(`• ${resolveClientName(bill, clients)} – ₹${money(bill.balance)} outstanding${age != null ? `; oldest bill ${age} days` : ''} – follow-up ${beforeDay(bill.nextFollowUpDate, today) ? `overdue since ${dateLabel(bill.nextFollowUpDate)}` : 'due today'}`);
  });
  highValue.slice(0, 3).forEach((bill) => {
    if (billingRows.some((line) => line.includes(resolveClientName(bill, clients)))) return;
    const age = daysSince(bill.proformaDate || bill.createdAt);
    billingRows.push(`• ${resolveClientName(bill, clients)} – ₹${money(bill.balance)} outstanding${age != null ? `; ${age} days old` : ''} – high-value collection attention`);
  });
  outstandingBills.filter((bill) => String(bill.status || '').toLowerCase() === 'draft').slice(0, 3).forEach((bill) => {
    billingRows.push(`• ${resolveClientName(bill, clients)} – ${bill.proformaNumber || 'Proforma'} ₹${money(bill.grandTotal)} for ${billWorkDescription(bill)} – draft not yet sent`);
  });

  const attentionRows = [];
  hearings.filter((matter) => sameDay(matter.nextHearingDate, today)).slice(0, 2).forEach((matter) => attentionRows.push(`• ${resolveClientName(matter, clients)} – hearing today${matter.nextHearingDate ? ` (${dateLabel(matter.nextHearingDate)})` : ''}`));
  overdue.slice(0, 3).forEach((row) => attentionRows.push(`• ${resolveClientName(row, clients)} – ${itemName(row)} overdue${row.dueDate ? ` since ${dateLabel(row.dueDate)}` : ''}`));
  oldOutstanding.filter((bill) => (daysSince(bill.proformaDate || bill.createdAt) || 0) >= 90).slice(0, 2).forEach((bill) => attentionRows.push(`• ${resolveClientName(bill, clients)} – ₹${money(bill.balance)} outstanding for ${daysSince(bill.proformaDate || bill.createdAt)} days`));

  const sections = [
    `*Pravah Morning Brief – ${today.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}*`,
    '',
    '*Priority Work for Today*',
    ...lines(priorityWork, (row) => lineForWork(row, clients), 8),
    '',
    '*Matters / Hearings*',
    ...lines(matterItems, (matter) => `• ${resolveClientName(matter, clients)} – ${matter.title || matter.assignmentType || 'Matter'} – ${matter.nextHearingDate ? `${sameDay(matter.nextHearingDate, today) ? 'hearing today' : `hearing ${dateLabel(matter.nextHearingDate)}`}` : `reply due ${dateLabel(matter.replyDueDate)}`}${matter.assignedToName ? ` – ${matter.assignedToName}` : ''}`, 6),
    '',
    '*Billing & Collections*',
    ...(billingRows.length ? billingRows.slice(0, 8) : ['• No billing or collection exception requiring attention today.']),
    '',
    '*Unbilled Work / Revenue Leakage*',
    ...lines(unbilled, (row) => `• ${resolveClientName(row, clients)} – ${itemName(row)} completed${row.completedAt ? ` ${daysSince(row.completedAt) === 0 ? 'today' : `${daysSince(row.completedAt)} day${daysSince(row.completedAt) === 1 ? '' : 's'} ago`}` : ''}; billing pending${row.assignedToName ? ` – ${row.assignedToName}` : ''}`, 6),
    '',
    '*Needs Partner Attention Today*',
    ...(attentionRows.length ? attentionRows.slice(0, 7) : ['• No exceptional item flagged for intervention today.']),
    '',
    '*Today at a Glance*',
    `Due Today: ${dueToday.length} | Overdue: ${overdue.length} | Hearings Today: ${hearings.filter((matter) => sameDay(matter.nextHearingDate, today)).length} | Follow-ups Due: ${followUpsDue.length} | Unbilled Completed Work: ${unbilled.length} | 90+ Day Outstanding: ₹${money(outstanding90Plus)}`,
    '', '', SYSTEM_FOOTER,
  ];
  return sections.join('\n');
}

function buildEveningBrief({ today, completedToday, billsRaisedToday, paymentsToday, overdue, dueTomorrow, hearings, unbilled, followUpsDue, outstandingBills, clients = [] }) {
  const totalOutstanding = totalAmount(outstandingBills, (item) => item.balance);
  const billsRaisedAmount = totalAmount(billsRaisedToday, (bill) => bill.grandTotal);
  const paymentsReceivedAmount = totalAmount(paymentsToday, ({ payment }) => payment.amount);

  const billingCollectionRows = [];
  billsRaisedToday.slice(0, 5).forEach((bill) => {
    billingCollectionRows.push(`• ${resolveClientName(bill, clients)} – ${bill.proformaNumber || 'Proforma'} ₹${money(bill.grandTotal)} raised today for ${billWorkDescription(bill)}; ${billDispatchLabel(bill)}`);
  });
  paymentsToday.slice(0, 5).forEach(({ bill, payment }) => {
    billingCollectionRows.push(`• ${resolveClientName(bill, clients)} – ₹${money(payment.amount)} payment received today against ${billWorkDescription(bill)} (${bill.proformaNumber || 'Bill'})`);
  });
  const noteworthyOutstanding = [...outstandingBills]
    .sort((a, b) => ((daysSince(b.proformaDate || b.createdAt) || 0) - (daysSince(a.proformaDate || a.createdAt) || 0)) || (b.balance - a.balance))
    .slice(0, 3);
  noteworthyOutstanding.forEach((bill) => {
    const age = daysSince(bill.proformaDate || bill.createdAt);
    billingCollectionRows.push(`• ${resolveClientName(bill, clients)} – ₹${money(bill.balance)} balance outstanding${age != null ? `; oldest bill ${age} days` : ''}`);
  });
  followUpsDue.slice(0, 2).forEach((bill) => {
    billingCollectionRows.push(`• ${resolveClientName(bill, clients)} – follow-up ${beforeDay(bill.nextFollowUpDate, today) ? `overdue since ${dateLabel(bill.nextFollowUpDate)}` : 'due today'}; ₹${money(bill.balance)} outstanding`);
  });

  const attentionRows = [];
  overdue.slice(0, 3).forEach((row) => attentionRows.push(`• ${resolveClientName(row, clients)} – ${itemName(row)} overdue${row.dueDate ? ` since ${dateLabel(row.dueDate)}` : ''}`));
  hearings.filter((matter) => sameDay(matter.nextHearingDate, addDays(today, 1))).slice(0, 2).forEach((matter) => attentionRows.push(`• ${resolveClientName(matter, clients)} – hearing tomorrow${matter.nextHearingDate ? ` (${dateLabel(matter.nextHearingDate)})` : ''}`));
  outstandingBills.filter((bill) => String(bill.status || '').toLowerCase() === 'draft').slice(0, 2).forEach((bill) => attentionRows.push(`• ${resolveClientName(bill, clients)} – ${bill.proformaNumber || 'Proforma'} ₹${money(bill.grandTotal)} raised for ${billWorkDescription(bill)} but not yet sent`));

  const sections = [
    `*Pravah Evening Brief – ${today.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}*`,
    '',
    '*Work Completed Today*',
    ...lines(completedToday, (row) => `• ${resolveClientName(row, clients)} – ${itemName(row)} completed by ${row.assignedToName || 'Staff'}${!row.proformaId ? '; billing pending' : ''}`, 7),
    '',
    '*Still Pending / Overdue*',
    ...lines([...overdue, ...dueTomorrow], (row) => lineForWork(row, clients), 7),
    '',
    '*Matters / Hearings*',
    ...lines(hearings, (matter) => `• ${resolveClientName(matter, clients)} – ${matter.title || matter.assignmentType || 'Matter'} – ${sameDay(matter.nextHearingDate, addDays(today, 1)) ? 'hearing tomorrow' : `hearing ${dateLabel(matter.nextHearingDate)}`}${matter.assignedToName ? ` – ${matter.assignedToName}` : ''}`, 5),
    '',
    '*Billing & Collections*',
    ...(billingCollectionRows.length ? billingCollectionRows.slice(0, 10) : ['• No billing or collection movement recorded today.']),
    '',
    '*Unbilled Work / Revenue Leakage*',
    ...lines(unbilled, (row) => `• ${resolveClientName(row, clients)} – ${itemName(row)} completed${row.completedAt ? ` ${daysSince(row.completedAt) === 0 ? 'today' : `${daysSince(row.completedAt)} day${daysSince(row.completedAt) === 1 ? '' : 's'} ago`}` : ''}; billing pending${row.assignedToName ? ` – ${row.assignedToName}` : ''}`, 6),
    '',
    '*Needs Partner Attention Tomorrow*',
    ...(attentionRows.length ? attentionRows.slice(0, 7) : ['• No exceptional item flagged for tomorrow.']),
    '',
    '*Today at a Glance*',
    `Completed: ${completedToday.length} | Overdue: ${overdue.length} | Bills Raised: ₹${money(billsRaisedAmount)} | Payments Received: ₹${money(paymentsReceivedAmount)} | Total Outstanding: ₹${money(totalOutstanding)}`,
    '', '', SYSTEM_FOOTER,
  ];
  return sections.join('\n');
}

